import { z } from 'zod';
import { UserRole, UserStatus } from './../enums/user';
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  isPasswordPolicySatisfied,
} from './password-policy';

const passwordLengthSchema =
  PASSWORD_MAX_LENGTH === -1
    ? z.string().min(PASSWORD_MIN_LENGTH)
    : z.string().min(PASSWORD_MIN_LENGTH).max(PASSWORD_MAX_LENGTH);

export const SignupValidatorSchema = z.object({
  accessCode: z.string().optional(),
  email: z.string(),
  password: passwordLengthSchema,
  confirmPassword: passwordLengthSchema,
  firstName: z.string(),
  lastName: z.string(),
  status: z.enum(UserStatus),
  roles: z.array(z.enum(UserRole)),
  confirmationReturnURLPath: z.string().optional(),
});

export type SignupValidator = z.infer<typeof SignupValidatorSchema>;

export const SignupFormValidatorSchema = z
  .object({
    accessCode: z.string().optional(),
    email: z
      .string()
      .min(1, 'api_authentication_signup_email_required')
      .email('invalid_email_validation_message'),
    password: z.string().min(1, 'api_authentication_password_required'),
    confirmPassword: z.string().min(1, 'api_authentication_password_required'),
    firstName: z
      .string()
      .min(1, 'api_authentication_signup_firstname_required'),
    lastName: z.string().min(1, 'api_authentication_signup_lastname_required'),
    termsAndConditions: z.boolean().refine((value) => value === true, {
      message: 'general_label_required',
    }),
    allowDirectMarketingFromSeaters: z.boolean().optional(),
    allowDirectMarketingFromPartners: z.boolean().optional(),
    fanGroupRules: z.boolean().optional(),
  })
  .superRefine((values, ctx) => {
    if (values.password.length > 0) {
      if (values.password.length < PASSWORD_MIN_LENGTH) {
        ctx.addIssue({
          message: 'api_authentication_signup_password_length',
          code: z.ZodIssueCode.custom,
          path: ['password'],
        });
      } else if (!isPasswordPolicySatisfied(values.password)) {
        ctx.addIssue({
          message: 'api_authentication_signup_password_content',
          code: z.ZodIssueCode.custom,
          path: ['password'],
        });
      }
    }

    if (
      values.password.length > 0 &&
      values.confirmPassword.length > 0 &&
      values.password !== values.confirmPassword
    ) {
      ctx.addIssue({
        message: 'confirm_password_validation_message',
        code: z.ZodIssueCode.custom,
        path: ['confirmPassword'],
      });
    }
    if (values.fanGroupRules !== undefined && !values.fanGroupRules) {
      ctx.addIssue({
        message: 'general_label_required',
        code: z.ZodIssueCode.custom,
        path: ['fanGroupRules'],
      });
    }
  });

export type SignupFormValidator = z.infer<typeof SignupFormValidatorSchema>;
